import { getExperimentTreatment } from '@ms/yammer-data/dist/domains/experiments/selectors';
import type { Store } from '@ms/yammer-data/dist/state/types';
import { ExperimentContextProvider } from '@ms/yammer-experiments-context';
import {
  useAndReportExperimentTreatment,
  useExperimentTreatmentWithoutReporting as useExperimentTreatment,
  useReportExperimentTreatmentCallback,
} from '@ms/yammer-hooks/dist/experiments';
import { useIsFeatureRolloutEnabled } from '@ms/yammer-hooks/dist/featureRollouts';
import { useGetUserConfirmation } from '@ms/yammer-page-route-telemetry';
import { LazyRelayAppWrapper } from '@ms/yammer-relay/dist/environment';
import { PageSectionRefsProvider } from '@ms/yammer-web-components/dist/app/PageSectionRefs';
import { AnnouncerProvider } from '@ms/yammer-web-core-ui/dist/components/Announcer';
import GriffelNoopRendererProvider from '@ms/yammer-web-core-ui/dist/components/GriffelNoopRendererProvider';
import { basenameForTeamsApp, isLoadedWithGlammerOrEngageDomain } from '@ms/yammer-web-support/dist/location';
import AppError, { AppErrorBoundary } from 'components/AppError';
import type { FC } from 'react';
import React from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { CompatRouter } from 'react-router-dom-v5-compat';
import type { YammerTeamsState } from 'state/types';

import App from '../App';
import EscapeKeyNavigationHandler from '../EscapeKeyNavigationHandler';

import { applyMainStyles } from './Main.styles.legacy';
import { getLazyRelayAppWrapperProps } from './getLazyRelayAppWrapperProps';

const experimentContext = {
  useReportExperimentTreatmentCallback,
  useIsFeatureRolloutEnabled,
  useExperimentTreatment,
  useAndReportExperimentTreatment,
  getExperimentTreatment,
};

interface MainProps {
  readonly store: Store<YammerTeamsState>;
  readonly error: unknown;
  readonly etag?: string;
}

const Main: FC<MainProps> = ({ store, error, etag }) => {
  const getUserConfirmation = useGetUserConfirmation();

  const routeBasename = isLoadedWithGlammerOrEngageDomain() ? basenameForTeamsApp : '';
  const children =
    error != null ? (
      <AppErrorBoundary>
        <AppError error={error} />
      </AppErrorBoundary>
    ) : (
      <App />
    );

  applyMainStyles();

  return (
    <GriffelNoopRendererProvider>
      <Provider store={store}>
        <ExperimentContextProvider {...experimentContext}>
          <LazyRelayAppWrapper {...getLazyRelayAppWrapperProps(store, etag)}>
            <HelmetProvider>
              <BrowserRouter basename={routeBasename} getUserConfirmation={getUserConfirmation}>
                <CompatRouter>
                  <PageSectionRefsProvider>
                    <EscapeKeyNavigationHandler>
                      <AnnouncerProvider>{children}</AnnouncerProvider>
                    </EscapeKeyNavigationHandler>
                  </PageSectionRefsProvider>
                </CompatRouter>
              </BrowserRouter>
            </HelmetProvider>
          </LazyRelayAppWrapper>
        </ExperimentContextProvider>
      </Provider>
    </GriffelNoopRendererProvider>
  );
};

export default Main;
